<script>
    import Icon from 'views/components/icon/Icon.svelte';
    import Spinner from 'views/components/spinner/Spinner.svelte'

    /* Changelog from Backbone's button:
        * buttonElement is removed, we shouldn't access elements directly
        * labelElement is removed, we shouldn't access elements directly
        suggestion: use label attribute directly
    */

    export let inline = false
    export let label = ''
    export let type = null
    export let title = null
    export let theme = 'primary'
    export let size = 'small'
    export let icon = null
    export let iconRight = null
    export let callback = null
    export let disabled = false
    export let hasSpinner = false

    /**
     * @deprecated Should use svelte properties instead.
     */
    export function enable() {
        console.warn('Calling deprecated enable method on button');

        disabled = false;

        if (hasSpinner) {
            hasSpinner = false;
        }
    }

    /**
     * @deprecated Should use svelte properties instead.
     * @param {boolean} startSpinner start spinner
     */
    export function disable(startSpinner = false) {
        console.warn('Calling deprecated disable method on button');

        disabled = true

        if (startSpinner) {
            hasSpinner = true
        }

        if (!startSpinner && hasSpinner) {
            hasSpinner = false;
        }
    }

    /**
     * @deprecated Should use svelte properties instead.
     * @param {boolean} newState force new state if param is present
     */
    export function toggle(newState) {
        console.warn('Calling deprecated toggle method on button');

        const state = newState === undefined ? disabled : newState

        if (state) {
            enable()
        } else {
            disable()
        }
    }

    /**
     * @deprecated Should use svelte properties instead.
     * @param {boolean} showSpinner show spinner if true, hide if false
     */
    export function toggleSpinner(showSpinner = false) {
        console.warn('Calling deprecated toggleSpinner method on button');

        hasSpinner = showSpinner
    }

    /**
     * @deprecated Should use svelte properties instead.
     * @param {string} newLabel new label for button
     */
    export function changeLabel(newLabel) {
        console.warn('Calling deprecated enable method on button');

        label = newLabel
    }

    // Listen to the disabled state change, if disabled is set to
    // false, which re-enables the button. We should also hide/remove
    // the spinner if its enabled
    $: changedDisableState(disabled)

    function handleClick(e) {
        if (disabled) {
            return;
        }

        if (callback) {
            callback(e);
        }
    }

    function changedDisableState(state) {
        if (!state && hasSpinner) {
            hasSpinner = false;
        }
    }

</script>

<div class="holder" class:is-inline={inline}>
    <button
        class={[
            'no-print', 'button',
            theme && `theme-${theme}`,
            size && `size-${size}`,
        ].join(' ')}
        {disabled}
        {type}
        {title}
        on:click={handleClick}
    >
        <span class="container">

            {#if icon}
                <span class="icon" data-button-icon>
                    <Icon name={icon}></Icon>
                </span>
            {/if}

            {#if label}
                <span>{label}</span>
            {/if}

            {#if iconRight}
                <span class="icon" data-button-icon>
                    <Icon name={iconRight}></Icon>
                </span>
            {/if}

            {#if hasSpinner}
                <span class="spinner">
                    <Spinner isInsideButton="true"></Spinner>
                </span>
            {/if}
        </span>
    </button>
</div>

<style lang="scss">/*
* Scut, a collection of Sass utilities
* to ease and improve our implementations of common style-code patterns.
* v1.0.1
* Docs at http://davidtheclark.github.io/scut
*/
/* TODO implement tooltip arrow */
.holder:not(.is-inline) {
  display: flex;
  flex-direction: column;
}

.holder.is-inline {
  display: contents;
}

.button {
  position: relative;
  justify-content: center;
}
.button:disabled:disabled {
  cursor: not-allowed;
  color: rgba(18, 45, 64, 0.5);
  background-color: hsl(27, 15%, 93%);
  border-color: hsl(40, 4%, 84%);
}
@media (hover: hover) {
  .button:not(:disabled):hover {
    background-color: hsl(205, 60%, 44%);
    border-color: hsl(205, 60%, 44%);
    color: #fff;
  }
  .button:not(:disabled):hover :global(svg) {
    fill: #fff;
  }
}
.button:not(:disabled):active {
  background-color: hsl(205, 58%, 38%);
  border-color: hsl(205, 58%, 38%);
  color: #fff;
}
.button:not(:disabled):active :global(svg) {
  fill: #fff;
}
.button:not(:disabled):focus:not(.button:not(:disabled):active):focus {
  box-shadow: 0 0 10px 0 rgba(17.6%, 48.4%, 70.4%, 0.5);
  outline: 0;
  border-color: hsl(205, 60%, 44%);
  text-decoration: underline;
}
.button.theme-primary {
  font-size: 14px;
  border-radius: 12px;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  transition: background 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
}
.button.theme-primary :global(svg) {
  width: 16px;
  height: 16px;
  transition: fill 0.1s cubic-bezier(0.215, 0.61, 0.355, 1);
  flex-shrink: 0;
}
.button.theme-primary {
  color: #fff;
  background-color: #122d40;
  border: solid 1px #122d40;
}
.button.theme-primary > :global(*) {
  display: inline-flex;
}
.button.theme-primary > :global(*:empty) {
  display: none;
}
.button.theme-secondary {
  font-size: 14px;
  border-radius: 12px;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  transition: background 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
}
.button.theme-secondary :global(svg) {
  width: 16px;
  height: 16px;
  transition: fill 0.1s cubic-bezier(0.215, 0.61, 0.355, 1);
  flex-shrink: 0;
}
.button.theme-secondary {
  background-color: #fff;
  color: #122d40;
  border: solid 1px hsl(40, 4%, 84%);
}
.button.theme-secondary :global(svg) {
  fill: #122d40;
}
.button.theme-secondary > :global(*) {
  display: inline-flex;
}
.button.theme-secondary > :global(*:empty) {
  display: none;
}
.button.theme-outlined {
  font-size: 14px;
  border-radius: 12px;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  transition: background 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
}
.button.theme-outlined :global(svg) {
  width: 16px;
  height: 16px;
  transition: fill 0.1s cubic-bezier(0.215, 0.61, 0.355, 1);
  flex-shrink: 0;
}
.button.theme-outlined {
  background-color: unset;
  color: hsl(205, 60%, 44%);
  border: solid 1px currentColor;
}
.button.theme-outlined > :global(*) {
  display: inline-flex;
}
.button.theme-outlined > :global(*:empty) {
  display: none;
}
.button.theme-transparent {
  font-size: 14px;
  border-radius: 12px;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  transition: background 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
}
.button.theme-transparent :global(svg) {
  width: 16px;
  height: 16px;
  transition: fill 0.1s cubic-bezier(0.215, 0.61, 0.355, 1);
  flex-shrink: 0;
}
.button.theme-transparent {
  background-color: transparent;
  color: unset;
}
.button.theme-transparent > :global(*) {
  display: inline-flex;
}
.button.theme-transparent > :global(*:empty) {
  display: none;
}
.button.theme-danger {
  font-size: 14px;
  border-radius: 12px;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  transition: background 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
}
.button.theme-danger :global(svg) {
  width: 16px;
  height: 16px;
  transition: fill 0.1s cubic-bezier(0.215, 0.61, 0.355, 1);
  flex-shrink: 0;
}
.button.theme-danger {
  background-color: #fc7787;
}
.button.theme-danger > :global(*) {
  display: inline-flex;
}
.button.theme-danger > :global(*:empty) {
  display: none;
}
.button.size-medium {
  font-size: 16px;
  padding: 12px 16px;
}
.button.size-medium :global(svg) {
  width: 20px;
  height: 20px;
}
.button .container {
  align-items: center;
  gap: 8px;
}
.button .container .icon {
  display: inline-flex;
}
.button .container .spinner {
  position: absolute;
  display: block;
  margin: 0 auto;
  left: 0;
  right: 0;
  top: 0;
  height: 100%;
}
.button .container .spinner :global(circle) {
  stroke: #122d40;
}</style>
